import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { FleetSection, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { Drawer } from '../../../../../shared/drawer/drawer'
import { TableAgInfinite } from '../../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat } from '../../../../../shared/table-builder/table-columns'
import { IconList } from '../../../../../shared/icon/icon'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { formatCount, formatDuration } from '../../../../../shared/sso-workspace/format'
import { agoText, dateTimeText, utcText } from '../../../../../shared/date/local-time'
import { getKeyRequest, listKeyRequests } from '../api'
import '../api-keys.scss'

// A key's request log (Task 15; ruling 10): its rows of the platform's
// request log over content's default window (the last 30 days), newest
// first, paged by content's cursor, already redacted, kept as long as the
// request log keeps requests. A row opens the request
// in a drawer (/org/api-keys/:keyID/requests/:requestLogID).

// The grid's block (TableAgInfinite's cacheBlockSize), so one block is one
// page; content allows up to 500.
const PAGE = 200

const first = (row, ...names) => {
  for (const name of names) if (row && row[name] !== undefined && row[name] !== null) return row[name]
  return undefined
}

export const statusTone = status => {
  const n = Number(status)
  if (n >= 500) return 'bad'
  if (n >= 400) return 'warn'
  if (n >= 200 && n < 300) return 'ok'
  return 'info'
}

// One row of GET /api-key/:id/requests (content's RequestRow) as the grid
// shows it: `id` is the log row's (it opens the request), `request_id` the
// request's own; a request no route matched has no route, so its path.
export const requestRow = r => {
  const method = first(r, 'method') || ''
  const route = first(r, 'route', 'path') || ''
  const status = Number(first(r, 'status')) || 0
  const durationMs = Number(first(r, 'duration_ms')) || 0
  const occurredAt = first(r, 'occurred_at') || ''
  return {
    id: String(first(r, 'id') || ''),
    requestID: first(r, 'request_id') || '',
    occurredAt,
    whenText: occurredAt ? agoText(occurredAt) : '',
    method,
    route,
    requestText: `${method} ${route}`.trim(),
    status,
    statusText: status ? String(status) : '',
    durationMs,
    durationText: formatDuration(durationMs)
  }
}

// GET /api-key/:id/requests pages by cursor (`next`), the grid by row
// index: each block start remembers the cursor that reaches it. A short
// page is the last.
export const requestsPager = (keyID, setError) => {
  const cursors = { 0: '' }
  return async (start, end) => {
    const cursor = cursors[start]
    if (cursor === undefined) return { data: [], count: start }
    try {
      const limit = Math.min(end - start, PAGE)
      const page = await listKeyRequests(keyID, cursor ? { cursor, limit } : { limit })
      const items = ((page && page.items) || []).map(requestRow)
      if (page && page.next && items.length === limit) {
        cursors[start + items.length] = page.next
        return { data: items, count: -1 }
      }
      return { data: items, count: start + items.length }
    } catch (err) {
      setError(serverMessage(err, "This key's requests couldn't be loaded."), err)
      return { data: [], count: start }
    }
  }
}

const whenCell = cell => (cell.data ? <span title={`${dateTimeText(cell.data.occurredAt, { seconds: true })} (${utcText(cell.data.occurredAt)})`}>{cell.data.whenText}</span> : '')
const requestCell = cell => (cell.data ? <span className="sw-mono">{cell.data.requestText}</span> : '')
const statusCell = cell => (cell.data ? <Pill tone={statusTone(cell.data.status)}>{cell.data.statusText}</Pill> : '')
const durationCell = cell => (cell.data ? cell.data.durationText : '')

const bodyText = body => {
  if (body === undefined || body === null || body === '') return ''
  return typeof body === 'string' ? body : JSON.stringify(body, null, 2)
}

// A body as content stored it: redacted, perhaps cut short, perhaps not
// stored at all (its note says why).
const Body = ({ title, body, note, truncated }) => {
  const text = bodyText(body)
  return (
    <section className="mb-3" aria-label={title}>
      <h3 className="h6">{title}</h3>
      {text ? <pre className="ak-body sw-mono">{text}</pre> : <p className="sw-hint m-0">{note || 'None stored.'}</p>}
      {text && truncated && <p className="sw-hint small m-0">Cut short: only the start of it is stored.</p>}
    </section>
  )
}

const NOT_FOUND = "This request isn't in the log (it may be older than the log keeps requests)."

// One request of the key's log, as content reads it (RequestDetail): its
// own organization and key only, query, headers and bodies with secrets
// removed.
export const RequestDrawer = ({ keyID, requestLogID, onClose }) => {
  const [state, setState] = useState({ loading: true })

  useEffect(() => {
    let live = true
    setState({ loading: true })
    getKeyRequest(keyID, requestLogID).then(
      d => { if (live) setState(d ? { detail: d } : { notFound: true }) },
      err => {
        if (!live) return
        setState(err && err.status === 404 ? { notFound: true } : { error: serverMessage(err, "This request couldn't be loaded."), failure: err })
      }
    )
    return () => { live = false }
  }, [keyID, requestLogID])

  const d = state.detail
  const row = d ? requestRow(d) : null
  const title = row && row.requestText ? row.requestText : 'Request'
  const headers = d && d.request_headers && typeof d.request_headers === 'object' ? Object.entries(d.request_headers) : []
  const rows = d
    ? [
      ['Time', row.occurredAt ? <time dateTime={row.occurredAt} title={utcText(row.occurredAt)}>{dateTimeText(row.occurredAt, { seconds: true })}</time> : null],
      ['Status', <Pill tone={statusTone(row.status)}>{row.statusText}</Pill>],
      ['Duration', row.durationText],
      ['Service', first(d, 'service')],
      ['Path', first(d, 'path') ? <span className="sw-mono">{first(d, 'path')}</span> : null],
      ['Query', first(d, 'query') ? <span className="sw-mono">{first(d, 'query')}</span> : null],
      ['IP address', first(d, 'ip') ? <span className="sw-mono">{first(d, 'ip')}</span> : null],
      ['Size', first(d, 'request_bytes') !== undefined ? `${formatCount(first(d, 'request_bytes'))} bytes in, ${formatCount(first(d, 'response_bytes') || 0)} bytes out` : null],
      ['Request ID', row.requestID ? <span className="sw-mono">{row.requestID}</span> : null]
    ].filter(([, v]) => v !== null && v !== undefined && v !== '')
    : []

  return (
    <Drawer open size="lg" title={title} onClose={onClose}>
      {state.loading && <p className="sw-hint m-0">Loading…</p>}
      {state.notFound && <p className="m-0">{NOT_FOUND}</p>}
      {state.error && <p className="text-danger small m-0" role="alert">{state.error}</p>}
      {state.error && <ErrorReference error={state.failure} className="mt-1" />}
      {d &&
        <div>
          {d.redaction_failed && <div className="sw-notice warn" role="status"><div>Removing secrets from this request failed, so its bodies were not stored.</div></div>}
          <p className="sw-hint small">Shown with secrets removed.</p>
          <dl className="sw-dl">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
          {headers.length > 0 &&
            <section className="mb-3" aria-label="Request headers">
              <h3 className="h6">Request headers</h3>
              <dl className="sw-dl">{headers.map(([k, v]) => <React.Fragment key={k}><dt className="sw-mono">{k}</dt><dd className="sw-mono">{Array.isArray(v) ? v.join(', ') : String(v)}</dd></React.Fragment>)}</dl>
            </section>}
          <Body title="Request body" body={d.request_body} note={d.request_body_note} truncated={d.request_body_truncated} />
          <Body title="Response body" body={d.response_body} note={d.response_body_note} truncated={d.response_body_truncated} />
        </div>}
    </Drawer>
  )
}

export const ApiKeyRequests = ({ keyID }) => {
  const history = useHistory()
  const match = useRouteMatch('/org/api-keys/:keyID/requests/:requestLogID')
  const [error, failure, setError] = useErrorLine()
  const [attempt, setAttempt] = useState(0)
  const tableRef = useRef()
  const getRows = useMemo(() => requestsPager(keyID, setError), [keyID, setError, attempt]) // eslint-disable-line react-hooks/exhaustive-deps

  const open = r => history.push(`/org/api-keys/${keyID}/requests/${encodeURIComponent(r.id)}`)
  const close = () => history.push({ pathname: `/org/api-keys/${keyID}`, search: '?tab=requests' })
  let requestLogID = null
  if (match) {
    try { requestLogID = decodeURIComponent(match.params.requestLogID) } catch (e) { requestLogID = match.params.requestLogID }
  }

  return (
    <FleetSection id="ak-requests" title="Requests" lede="Every request made with this key in the last 30 days, newest first, with secrets removed, for as long as the request log keeps them.">
      {error && <Notice tone="bad" role="alert">{error} <button type="button" className="btn btn-sm sw-btn ms-1" onClick={() => { setError(null); setAttempt(n => n + 1) }}>Try again</button><ErrorReference error={failure} /></Notice>}
      {!error &&
        <FleetGrid title="Requests" subtitle="Newest first" tableRef={tableRef} flush>
          <TableAgInfinite key={attempt} ref={tableRef} queryKey="" onGetRows={getRows} onRowClicked={open} hideSearch
            emptyState={{ Icon: IconList, title: 'No requests yet', message: 'Requests made with this key in the last 30 days show here.' }}>
            <ColumnFormat field="whenText" header="When" sortable={false} fit="content" render={whenCell} />
            <ColumnFormat field="requestText" header="Request" sortable={false} flex={2} render={requestCell} />
            <ColumnFormat field="statusText" header="Status" sortable={false} fit="content" render={statusCell} />
            <ColumnFormat field="durationText" header="Duration" sortable={false} fit="content" render={durationCell} />
          </TableAgInfinite>
        </FleetGrid>}
      {requestLogID && <RequestDrawer keyID={keyID} requestLogID={requestLogID} onClose={close} />}
    </FleetSection>
  )
}
